import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Link, Text, Title } from '@nimbus-ds/components';
import {
  MapPin,
  Bookmark,
  LayoutList,
  CopyCheck,
  Tag,
  ShoppingCart,
  CalendarDays,
} from 'lucide-react';
import LanguageSelector from '../LanguageSelector/LanguageSelector';

const arrowUpRightIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5.5 4.5h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.5 4.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CAROUSEL_APPS_COUNT = 7;
const CAROUSEL_VISIBLE = 3;
const CAROUSEL_MAX_INDEX = CAROUSEL_APPS_COUNT - CAROUSEL_VISIBLE;

const CAROUSEL_APP_ICONS: Record<number, React.ElementType> = {
  1: MapPin,
  2: Bookmark,
  3: LayoutList,
  4: CopyCheck,
  5: Tag,
  6: ShoppingCart,
  7: CalendarDays,
};

const CAROUSEL_APP_URLS: Record<number, string> = {
  1: 'https://www.tiendanube.com/tienda-aplicaciones-nube/mapa-avanzado-lerentools?q=nimbus',
  2: 'https://www.tiendanube.com/tienda-aplicaciones-nube/cucardas-personalizadas-lerentools?q=nimbus',
  3: 'https://www.tiendanube.com/tienda-aplicaciones-nube/estados-personalizados-ordenes-notificaciones-lerentools?q=nimbus',
  4: 'https://www.tiendanube.com/tienda-aplicaciones-nube/acciones-masivas-automaticas-tiendanube-lerentools?q=nimbus',
  5: 'https://www.tiendanube.com/tienda-aplicaciones-nube/etiquetas-personalizadas-de-ordenes-para-tiendanube-lerentools?q=nimbus',
  6: 'https://www.tiendanube.com/tienda-aplicaciones-nube/reglas-de-negocio-avanzadas-lt?q=nimbus',
  7: 'https://www.tiendanube.com/tienda-aplicaciones-nube/preorder-personalizados-lerentools?q=nimbus',
};

const carouselArrowLeft = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const carouselArrowRight = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CAROUSEL_AUTO_INTERVAL_MS = 4000;
const CAROUSEL_PAUSE_AFTER_MANUAL_MS = 10000;

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselHover, setCarouselHover] = useState(false);
  const carouselPausedUntilRef = useRef<number>(0);

  const redirectUrl = import.meta.env.VITE_REDIRECT_URL || 'https://app.lerentools.com';

  useEffect(() => {
    const id = setInterval(() => {
      if (carouselHover) return;
      if (Date.now() < carouselPausedUntilRef.current) return;
      setCarouselIndex((i) => (i >= CAROUSEL_MAX_INDEX ? 0 : i + 1));
    }, CAROUSEL_AUTO_INTERVAL_MS);
    return () => clearInterval(id);
  }, [carouselHover]);

  const handleCarouselPrev = () => {
    carouselPausedUntilRef.current = Date.now() + CAROUSEL_PAUSE_AFTER_MANUAL_MS;
    setCarouselIndex((i) => Math.max(0, i - 1));
  };

  const handleCarouselNext = () => {
    carouselPausedUntilRef.current = Date.now() + CAROUSEL_PAUSE_AFTER_MANUAL_MS;
    setCarouselIndex((i) => Math.min(CAROUSEL_MAX_INDEX, i + 1));
  };

  const openExternal = (url: string) => {
    try {
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      if (window.top) (window.top as Window).location.href = url;
      else window.location.href = url;
    }
  };

  const handleRedirect = () => {
    setIsRedirecting(true);
    openExternal(redirectUrl);
  };

  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      padding="2"
      className="landing-page-wrap"
    >
      <Box width="100%" maxWidth="1280px" paddingTop="3">
        <Box
          width="100%"
          backgroundColor="neutral-surface"
          borderRadius="4"
          padding="5"
          boxShadow="2"
          className="landing-main-card"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            gap="8"
          >
            <Box display="flex" flexDirection="row" gap="8" alignItems="stretch" flexWrap="wrap">
              <Box flex="1" width="100%" minWidth="280px" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap="4"
                  width="100%"
                  maxWidth="480px"
                  paddingLeft="3"
                  paddingRight="3"
                  style={{ lineHeight: 1.2 }}
                >
                  <Box
                    display="inline-flex"
                    backgroundColor="neutral-surface"
                    borderRadius="3"
                    paddingLeft="3"
                    paddingRight="3"
                    paddingTop="2"
                    paddingBottom="2"
                    boxShadow="1"
                  >
                    <LanguageSelector />
                  </Box>

                  <Box>
                    <Title as="h1" fontSize="h2" fontWeight="bold" color="neutral-textHigh" className="landing-hero-title" style={{ margin: 0 }}>
                      {t('landing.title')}
                      <Box as="span" display="block" marginTop="0-5" style={{ marginBottom: 0 }}>{t('landing.titleBrand')}</Box>
                    </Title>
                  </Box>

                  <Text fontSize="base" color="neutral-textLow" lineHeight="highlight">
                    {t('landing.subtitle')}
                  </Text>

                  <Box width="100%" maxWidth="320px">
                    <Button
                      appearance="primary"
                      onClick={handleRedirect}
                      disabled={isRedirecting}
                      fullWidth
                      size="large"
                      aria-busy={isRedirecting}
                      aria-live="polite"
                      aria-label={isRedirecting ? t('landing.loading') : undefined}
                      className={isRedirecting ? 'landing-cta-loading' : undefined}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      {isRedirecting ? t('landing.loading') : t('landing.redirectButton')}
                      <img src="/images/Rocket-white.png" alt="" aria-hidden="true" className="landing-cta-rocket-icon" />
                    </Button>
                  </Box>

                  <Text as="span" fontSize="caption" color="neutral-textLow" className="landing-auth-links-wrap">
                    {t('landing.noAccount')}{' '}
                    <Link
                      as="a"
                      href="https://app.lerentools.com/login"
                      target="_blank"
                      rel="noopener noreferrer"
                      appearance="neutral"
                      className="landing-auth-link"
                      style={{ color: '#2179ff', textDecoration: 'none' }}
                    >
                      <Text as="span" fontSize="caption" style={{ color: '#2179ff' }}>
                        {t('landing.login')}
                      </Text>
                    </Link>
                    .
                  </Text>

                  <Link
                    as="a"
                    href="https://docs.lerentools.com/inicio"
                    target="_blank"
                    rel="noopener noreferrer"
                    appearance="neutral"
                    textDecoration="none"
                  >
                    <Box display="flex" alignItems="center" gap="2">
                      <Text as="span" fontSize="caption" color="neutral-textLow">
                        {t('landing.moreInfo')}
                      </Text>
                      <span aria-hidden="true">{arrowUpRightIcon}</span>
                    </Box>
                  </Link>
                </Box>
              </Box>

              <div className="landing-cards-grid">
                {[
                  { id: 1, image: 'Star.png' },
                  { id: 2, image: 'Rayo.png' },
                  { id: 3, image: 'Flecha.png' },
                  { id: 4, image: 'Escudo.png' },
                  { id: 5, image: 'Rocket.png' },
                  { id: 6, image: 'Auri.png' },
                ].map(({ id, image }) => (
                  <div key={id} className="landing-feature-card">
                    <Card backgroundColor="neutral-surface" padding="base">
                    <Box display="flex" flexDirection="column" minHeight="0" height="100%">
                      <Box
                        className="landing-card-illustration"
                        width="100%"
                        flexShrink="0"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        marginBottom="3"
                        overflow="hidden"
                      >
                        <img src={`/images/cards/${image}`} alt={t(`landing.cards.${id}.imageAlt`)} className="landing-card-illustration-img" />
                      </Box>
                      <Box marginBottom="2">
                        <Title as="h2" fontSize="h5" fontWeight="bold" color="neutral-textHigh" style={{ margin: 0 }}>
                          {t(`landing.cards.${id}.title`)}
                        </Title>
                      </Box>
                      <Box flex="1" display="flex" flexDirection="column" minHeight="0">
                        <Text fontSize="base" color="neutral-textLow" lineHeight="highlight">
                          {t(`landing.cards.${id}.description`)}
                        </Text>
                      </Box>
                    </Box>
                    </Card>
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        </Box>

        <div className="landing-carousel-section">
          <div className="landing-carousel-header">
            <Title as="h2" fontSize="h3" fontWeight="bold" color="neutral-textHigh" style={{ margin: 0, marginBottom: '0.5rem' }}>
              {t('landing.carousel.title')}
            </Title>
            <Text fontSize="base" color="neutral-textLow" lineHeight="highlight">
              {t('landing.carousel.description')}
            </Text>
          </div>

          <div
            className="landing-carousel-wrapper"
            onMouseEnter={() => setCarouselHover(true)}
            onMouseLeave={() => setCarouselHover(false)}
          >
            <button
              type="button"
              onClick={handleCarouselPrev}
              disabled={carouselIndex === 0}
              className="landing-carousel-arrow landing-carousel-arrow-left"
              aria-label={t('landing.carousel.prev') as string}
            >
              {carouselArrowLeft}
            </button>

            <div className="landing-carousel-viewport">
              <div
                className="landing-carousel-track"
                style={{ transform: `translateX(-${carouselIndex * (100 / CAROUSEL_VISIBLE)}%)` }}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((id) => (
                  <div key={id} className="landing-carousel-slot">
                    <div className="landing-carousel-card">
                      <Card backgroundColor="neutral-surface" padding="base">
                        <div className="landing-carousel-card-inner">
                          <div className="landing-card-illustration landing-carousel-card-image">
                            {(() => {
                              const Icon = CAROUSEL_APP_ICONS[id];
                              return Icon ? <Icon size={64} strokeWidth={2} className="landing-carousel-app-icon" aria-hidden /> : null;
                            })()}
                          </div>
                          <div className="landing-carousel-card-title">
                            <Title as="h3" fontSize="h4" fontWeight="bold" color="neutral-textHigh" style={{ margin: 0 }}>
                              {t(`landing.carousel.apps.${id}.title`)}
                            </Title>
                          </div>
                          <div className="landing-carousel-card-desc">
                            <Text fontSize="base" color="neutral-textLow" lineHeight="highlight">
                              {t(`landing.carousel.apps.${id}.description`)}
                            </Text>
                          </div>
                          <div className="landing-carousel-card-divider" />
                          <div className="landing-carousel-card-button-wrap">
                            <Button
                              appearance="primary"
                              size="medium"
                              fullWidth
                              onClick={() => openExternal(CAROUSEL_APP_URLS[id])}
                            >
                              {t('landing.carousel.viewApp')}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleCarouselNext}
              disabled={carouselIndex >= CAROUSEL_MAX_INDEX}
              className="landing-carousel-arrow landing-carousel-arrow-right"
              aria-label={t('landing.carousel.next') as string}
            >
              {carouselArrowRight}
            </button>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default LandingPage;
